import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Sparkles, Zap } from "lucide-react";
import Logo, { LogoMark } from "@/components/brand/Logo";

const FEATURES_ES = [
  { icon: Sparkles, title: "Un solo panel", body: "CRM, proyectos, facturación, hosting y marketing en un solo lugar." },
  { icon: ShieldCheck, title: "Multi-tenant seguro", body: "Datos aislados por empresa y roles granulares para cada equipo." },
  { icon: Zap, title: "IA integrada", body: "Genera cotizaciones, resúmenes y respuestas al instante." },
];
const FEATURES_EN = [
  { icon: Sparkles, title: "One control plane", body: "CRM, projects, billing, hosting and marketing in a single workspace." },
  { icon: ShieldCheck, title: "Secure multi-tenant", body: "Isolated data per company with granular role-based access." },
  { icon: Zap, title: "AI built-in", body: "Generate quotes, summaries and replies in seconds." },
];

export default function Login() {
  const { login } = useAuth();
  const { t, lang, setLang } = useLang();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const features = lang === "es" ? FEATURES_ES : FEATURES_EN;

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await login(email, password);
      toast.success(`${lang === "es" ? "Hola" : "Welcome"}, ${res.user.name || res.user.email}`);
      if (res.must_change_password || res.user?.must_change_password) {
        navigate("/change-password", { replace: true });
      } else {
        navigate("/app");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-brand-bg grid grid-cols-1 lg:grid-cols-2 relative overflow-hidden" data-testid="login-page">
      {/* animated ambient blobs */}
      <div aria-hidden className="pointer-events-none absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full opacity-30 blur-3xl animate-blob-a" style={{ background: "radial-gradient(circle, #2563EB 0%, transparent 60%)" }} />
      <div aria-hidden className="pointer-events-none absolute -bottom-52 -right-32 w-[560px] h-[560px] rounded-full opacity-30 blur-3xl animate-blob-b" style={{ background: "radial-gradient(circle, #38BDF8 0%, transparent 60%)" }} />

      {/* Left panel: form */}
      <div className="relative flex flex-col justify-between p-6 sm:p-10 lg:p-12 border-r border-slate-800">
        <div className="flex items-center justify-between animate-fade-in-up">
          <Logo withGlow />
          <button
            onClick={() => setLang(lang === "es" ? "en" : "es")}
            data-testid="login-lang-toggle"
            className="text-[11px] uppercase tracking-[0.2em] text-slate-400 hover:text-white transition-colors"
          >
            {lang.toUpperCase()}
          </button>
        </div>

        <div className="max-w-md w-full mx-auto lg:mx-0">
          <div className="animate-fade-in-up delay-1">
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full border border-slate-800 bg-slate-900/60 text-[10px] uppercase tracking-[0.2em] text-sky-400">
              {lang === "es" ? "Bienvenido de nuevo" : "Welcome back"}
            </div>
            <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.05]">
              {lang === "es" ? "Inicia sesión en" : "Sign in to"}<br />
              <span className="bg-clip-text text-transparent" style={{ backgroundImage: "linear-gradient(90deg, #2563EB, #38BDF8)" }}>Yosi Media Studio</span>
            </h1>
            <p className="mt-3 text-sm text-slate-400">
              {lang === "es"
                ? "Tu sistema operativo multi-tenant para agencias digitales."
                : "Your multi-tenant operating system for digital agencies."}
            </p>
          </div>

          <form onSubmit={onSubmit} className="mt-8 space-y-4 animate-fade-in-up delay-2">
            <label className="block">
              <span className="overline block mb-1.5 text-slate-500">{t("common.email")}</span>
              <input
                data-testid="login-email"
                type="email" required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full h-11 bg-slate-900/60 border border-slate-800 rounded-lg px-3.5 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/30 transition"
                placeholder="admin@yosimedia.com"
              />
            </label>
            <label className="block">
              <span className="overline block mb-1.5 text-slate-500">{t("common.password")}</span>
              <input
                data-testid="login-password"
                type="password" required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full h-11 bg-slate-900/60 border border-slate-800 rounded-lg px-3.5 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/30 transition"
                placeholder="••••••••"
              />
            </label>
            <button
              data-testid="login-submit"
              type="submit" disabled={loading}
              className="btn-scale w-full h-11 rounded-lg font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-70 shadow-lg shadow-blue-600/20"
              style={{ backgroundImage: "linear-gradient(135deg, #2563EB, #38BDF8)" }}
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {t("auth.login_btn")}
            </button>

            <div className="relative py-1">
              <div className="absolute inset-x-0 top-1/2 h-px bg-slate-800" />
              <div className="relative text-center text-[11px] text-slate-500">
                <span className="bg-brand-bg px-2">{t("common.or")}</span>
              </div>
            </div>

            <button
              data-testid="login-google"
              type="button" onClick={google}
              className="btn-scale w-full h-11 rounded-lg border border-slate-700 bg-slate-900/40 hover:bg-slate-900 text-white flex items-center justify-center gap-2 transition"
            >
              <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#EA4335" d="M12 10.2v3.9h5.5c-.24 1.3-1.6 3.8-5.5 3.8-3.3 0-6-2.75-6-6.15S8.7 5.6 12 5.6c1.9 0 3.15.8 3.87 1.48l2.63-2.55C16.9 3.07 14.7 2 12 2 6.98 2 3 6.02 3 11s3.98 9 9 9c5.2 0 8.63-3.65 8.63-8.8 0-.6-.07-1.05-.15-1.5H12z"/></svg>
              {t("auth.google_btn")}
            </button>

            <div className="text-center pt-2">
              <Link to="/register" data-testid="login-goto-register" className="text-sm text-slate-400 hover:text-white transition-colors">
                {t("auth.go_register")}
              </Link>
            </div>
          </form>
        </div>

        <div className="text-[11px] text-slate-600 animate-fade-in-up delay-3">
          © {new Date().getFullYear()} Yosi Media Studio · {lang === "es" ? "Todos los derechos reservados." : "All rights reserved."}
        </div>
      </div>

      {/* Right panel: brand hero */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 grid-bg overflow-hidden">
        <div aria-hidden className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(1200px 500px at 80% -10%, rgba(37,99,235,0.25), transparent), radial-gradient(1000px 500px at 20% 110%, rgba(56,189,248,0.18), transparent)" }} />
        <div className="relative flex items-center justify-between animate-fade-in-up">
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full border border-slate-800 bg-slate-900/60">
            <span className="w-1.5 h-1.5 rounded-full bg-sky-400 glow-green" />
            <span className="text-[10px] uppercase tracking-[0.2em] text-slate-300">Yosi Media · Studio</span>
          </div>
          <LogoMark size={44} withGlow />
        </div>

        <div className="relative max-w-lg animate-fade-in-up delay-1">
          <h2 className="font-display text-4xl xl:text-5xl font-semibold tracking-tight text-white leading-[1.08]">
            {lang === "es" ? (
              <>Diseñado para escalar con <span className="bg-clip-text text-transparent" style={{ backgroundImage: "linear-gradient(90deg, #2563EB, #38BDF8)" }}>miles de empresas</span>.</>
            ) : (
              <>Built to scale with <span className="bg-clip-text text-transparent" style={{ backgroundImage: "linear-gradient(90deg, #2563EB, #38BDF8)" }}>thousands of companies</span>.</>
            )}
          </h2>
          <p className="mt-4 text-slate-400">
            {lang === "es"
              ? "Todo lo que una agencia digital necesita para operar, cobrar y crecer — con IA nativa desde el día uno."
              : "Everything a digital agency needs to operate, get paid and grow — with AI built in from day one."}
          </p>
        </div>

        <div className="relative grid grid-cols-1 gap-3 max-w-lg">
          {features.map((f) => (
            <div key={f.title} className={`animate-fade-in-up flex items-start gap-3 rounded-xl border border-slate-800 bg-slate-900/50 p-4`}>
              <div className="w-9 h-9 rounded-lg grid place-items-center shrink-0" style={{ background: "linear-gradient(135deg, rgba(37,99,235,0.18), rgba(56,189,248,0.18))" }}>
                <f.icon className="w-4 h-4 text-sky-300" />
              </div>
              <div>
                <div className="text-sm font-semibold text-white">{f.title}</div>
                <div className="text-xs text-slate-400 mt-0.5">{f.body}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
